import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Link,
    IconButton,
    ButtonGroup,
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { CalendarEvent, CRMEntry } from '../../../models/CRMEntry';
import moment from 'moment-timezone';

interface MeetingDialogProps {
    open: boolean;
    onClose: () => void;
    event: CalendarEvent;
    crmEntry: CRMEntry;
    onEventUpdate: () => void;
    userTimezone: string;
}

// Add this constant at the top of the file after imports
const US_TIMEZONES = [
    { name: 'US/Eastern', label: 'Eastern Time' },
    { name: 'US/Central', label: 'Central Time' },
    { name: 'US/Mountain', label: 'Mountain Time' },
    { name: 'US/Pacific', label: 'Pacific Time' },
    { name: 'US/Alaska', label: 'Alaska Time' },
    { name: 'US/Hawaii', label: 'Hawaii Time' }
];

export const MeetingDialog: React.FC<MeetingDialogProps> = ({
    open,
    onClose,
    event,
    crmEntry,
    onEventUpdate,
    userTimezone
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState(userTimezone);

    // Convert event times to selected timezone
    const eventInTimezone = {
        startTime: moment(event.startTime.toDate()).tz(selectedTimezone),
        endTime: moment(event.endTime.toDate()).tz(selectedTimezone)
    };

    const [editedEvent, setEditedEvent] = useState({
        title: event.title,
        date: eventInTimezone.startTime.format('YYYY-MM-DD'),
        time: eventInTimezone.startTime.format('HH:mm'),
        duration: Math.round((event.endTime.toDate().getTime() - event.startTime.toDate().getTime()) / (60 * 1000)),
        location: event.location || '',
        timezone: selectedTimezone
    });

    const handleSave = async () => {
        setLoading(true);
        try {
            // Validate required fields
            if (!editedEvent.title || !editedEvent.date || !editedEvent.time) {
                throw new Error('Please fill in all required fields');
            }

            // Create date in selected timezone
            const startMoment = moment.tz(`${editedEvent.date} ${editedEvent.time}`, selectedTimezone);
            const endMoment = moment(startMoment).add(editedEvent.duration, 'minutes');

            // Convert to UTC for storage
            const startDate = startMoment.toDate();
            const endDate = endMoment.toDate();

            // Validate dates
            if (!startMoment.isValid() || !endMoment.isValid()) {
                throw new Error('Invalid date or time format');
            }

            if (startDate < new Date()) {
                throw new Error('Meeting cannot be scheduled in the past');
            }

            const updatedEvent = {
                id: event.id,
                title: editedEvent.title.trim(),
                startTime: Timestamp.fromDate(startDate),
                endTime: Timestamp.fromDate(endDate),
                location: editedEvent.location.trim() || null
            };

            // Get current events array or initialize empty array
            const currentEvents = Array.isArray(crmEntry.calendarEvents) ? crmEntry.calendarEvents : [];

            // Create new events array with updated event
            const updatedEvents = currentEvents.map(e =>
                e.id === event.id ? updatedEvent : e
            );

            const docRef = doc(db, 'crmEntries', crmEntry.id);
            await updateDoc(docRef, {
                calendarEvents: updatedEvents,
                lastActivityAt: Timestamp.now()
            });

            onEventUpdate();
            setIsEditing(false);
            onClose();
        } catch (error) {
            console.error('Error updating event:', error);
            alert(error instanceof Error ? error.message : 'Failed to update meeting');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this meeting?')) return;

        setLoading(true);
        try {
            // Filter out the event from calendarEvents array
            const updatedEvents = (crmEntry.calendarEvents || []).filter(e => e.id !== event.id);

            const docRef = doc(db, 'crmEntries', crmEntry.id);
            await updateDoc(docRef, {
                calendarEvents: updatedEvents,
                lastActivityAt: Timestamp.now()
            });

            onEventUpdate();
            onClose();
        } catch (error) {
            console.error('Error deleting event:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEditing ? 'Edit Meeting' : 'Meeting Details'}
                    <Box>
                        {!isEditing && (
                            <IconButton onClick={() => setIsEditing(true)} size="small">
                                <EditIcon />
                            </IconButton>
                        )}
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {isEditing ? (
                        <>
                            <TextField
                                fullWidth
                                label="Title"
                                value={editedEvent.title}
                                onChange={(e) => setEditedEvent(prev => ({ ...prev, title: e.target.value }))}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                    value={selectedTimezone}
                                    label="Timezone"
                                    onChange={(e) => {
                                        const newTimezone = e.target.value;
                                        setSelectedTimezone(newTimezone);
                                        // Convert existing date/time to new timezone
                                        const currentMoment = moment.tz(`${editedEvent.date} ${editedEvent.time}`, editedEvent.timezone);
                                        const newMoment = currentMoment.tz(newTimezone);
                                        setEditedEvent(prev => ({
                                            ...prev,
                                            date: newMoment.format('YYYY-MM-DD'),
                                            time: newMoment.format('HH:mm'),
                                            timezone: newTimezone
                                        }));
                                    }}
                                >
                                    {US_TIMEZONES.map(tz => (
                                        <MenuItem key={tz.name} value={tz.name}>
                                            {tz.label} ({moment.tz(tz.name).format('z')})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Date"
                                type="date"
                                value={editedEvent.date}
                                onChange={(e) => setEditedEvent(prev => ({ ...prev, date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                label="Time"
                                type="time"
                                value={editedEvent.time}
                                onChange={(e) => setEditedEvent(prev => ({ ...prev, time: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                            />
                            <ButtonGroup variant="outlined" fullWidth>
                                {[15, 30, 45, 60].map((mins) => (
                                    <Button
                                        key={mins}
                                        onClick={() => setEditedEvent(prev => ({ ...prev, duration: mins }))}
                                        variant={editedEvent.duration === mins ? 'contained' : 'outlined'}
                                    >
                                        {mins === 60 ? '1 hr' : `${mins}m`}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <TextField
                                fullWidth
                                label="Location (Optional)"
                                value={editedEvent.location}
                                onChange={(e) => setEditedEvent(prev => ({ ...prev, location: e.target.value }))}
                            />
                        </>
                    ) : (
                        <>
                            <Typography variant="h6">{event.title}</Typography>
                            <Typography>
                                📅 {eventInTimezone.startTime.format('LL')} {eventInTimezone.startTime.format('LT')} - {eventInTimezone.endTime.format('LT')} ({moment.tz(selectedTimezone).format('z')})
                            </Typography>
                            {event.location && (
                                <Typography>
                                    📍 {event.location}
                                </Typography>
                            )}
                            <Link
                                component={RouterLink}
                                to={`/crm/${crmEntry.id}`}
                                underline="hover"
                                sx={{ mt: 2 }}
                            >
                                View CRM Entry: {crmEntry.companyName}
                            </Link>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {isEditing ? (
                    <>
                        <Button onClick={() => setIsEditing(false)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} variant="contained" disabled={loading}>
                            Save Changes
                        </Button>
                    </>
                ) : (
                    <Button
                        onClick={handleDelete}
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={loading}
                    >
                        Delete Meeting
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}; 